import type { Lesson } from './courses';

/**
 * Parcours 6 : Referral & Viralite — 5 lecons.
 */
export const REFERRAL_VIRALITY_LESSONS: Lesson[] = [
  {
    slug: 'referral-basics',
    title: "Le pouvoir du bouche-a-oreille",
    duration: '4 min',
    glossaryTerms: ['referral-network', 'roi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le referral (parrainage) est le mecanisme par lequel vos clients existants recommandent votre produit a leur entourage. C'est la forme la plus ancienne et la plus efficace de marketing.\n\nLe bouche-a-oreille fonctionne parce qu'il repose sur la confiance : quand un collegue vous recommande un outil, vous lui faites davantage confiance qu'a une publicite.\n\nDans le monde SaaS, le referral prend souvent la forme de programmes structures : invitations, codes promo, recompenses pour les parrains et les filleuls.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les clients acquis par referral ont une LTV (Lifetime Value) 16% superieure a ceux acquis par d'autres canaux (Harvard Business Review). Ils sont aussi 4x plus susceptibles de devenir eux-memes des parrains, creant un effet boule de neige.\n\nLe cout d'acquisition par referral est generalement 5 a 10x inferieur a celui de la publicite payante. C'est le canal avec le meilleur ROI quand il est bien execute.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez vos promoteurs** — Quels clients sont les plus satisfaits ? (NPS >= 9)\n2. **Creez un programme simple** — Le parrain et le filleul doivent y gagner\n3. **Reduisez les frictions** — Un lien de parrainage en 1 clic, pas un formulaire\n4. **Communiquez regulierement** — Rappelez le programme via email, in-app, etc.\n5. **Mesurez le K-factor** — Nombre moyen de nouveaux utilisateurs generes par parrain",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Referral Network** vous permet de creer et gerer votre programme de parrainage. Definissez les recompenses, generez des liens de parrainage uniques et suivez les performances de chaque ambassadeur.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
  {
    slug: 'referral-programs',
    title: 'Concevoir un programme de parrainage',
    duration: '4 min',
    glossaryTerms: ['referral-network', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Un programme de parrainage structure transforme le bouche-a-oreille naturel en canal d'acquisition previsible. Il repose sur 3 piliers :\n\n- **Incitation** — Quelle recompense pour le parrain et le filleul ? (reduction, credit, fonctionnalite bonus)\n- **Mecanisme** — Comment partager ? (lien unique, code promo, invitation email)\n- **Suivi** — Comment tracker les conversions et distribuer les recompenses ?\n\nLes meilleurs programmes sont bilateraux : les deux parties y gagnent. Dropbox offrait 500 Mo supplementaires au parrain ET au filleul.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un programme de parrainage bien concu peut representer 20 a 35% de vos nouvelles acquisitions. Les ambassadeurs (Ambassadors) actifs deviennent vos meilleurs commerciaux : ils connaissent le produit, parlent le langage de vos prospects et ont une credibilite naturelle.\n\nLes programmes bilateraux ont un taux de participation 2x superieur aux programmes unilateraux.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Choisissez le type de recompense** — Financiere (remise, credit), fonctionnelle (feature unlock) ou symbolique (badge, statut)\n2. **Definissez les conditions** — Quand la recompense est-elle delivree ? (inscription, premier achat, abonnement actif 30j)\n3. **Creez les assets** — Landing page, email d'invitation, texte de partage\n4. **Lancez en beta** — Testez avec vos meilleurs clients avant le lancement public\n5. **Iterez** — Ajustez les recompenses et mecanismes selon les resultats",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Dans le **Referral Network**, configurez votre programme : type de recompense, conditions de validation, textes de partage. Chaque ambassadeur recoit un lien unique et un tableau de bord de ses performances.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
  {
    slug: 'viral-loops',
    title: 'Les boucles virales (Viral Loops)',
    duration: '3 min',
    glossaryTerms: ['referral-network', 'pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Une boucle virale (Viral Loop) est un mecanisme ou l'utilisation meme du produit genere de nouveaux utilisateurs. Contrairement au referral classique (qui necessite une action deliberee de parrainage), la boucle virale est integree a l'experience produit.\n\nExemples celebres :\n- **Calendly** — Chaque invitation de reunion expose le destinataire au produit\n- **Notion** — Les pages partagees attirent de nouveaux utilisateurs\n- **Slack** — Inviter un collegue est necessaire pour utiliser le produit\n\nLa metrique cle : le **K-factor**. Si K > 1, chaque utilisateur genere en moyenne plus d'un nouvel utilisateur — la croissance est exponentielle.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les boucles virales generent une croissance composee : plus vous avez d'utilisateurs, plus la boucle s'accelere. C'est le mecanisme de croissance le plus puissant qui existe, car il ne depend pas du budget marketing.\n\nLes produits avec un K-factor > 0.5 (meme sans atteindre 1) beneficient d'un effet amplificateur significatif sur leur croissance organique.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez votre boucle naturelle** — Quelle action utilisateur expose votre produit a de nouvelles personnes ?\n2. **Reduisez les frictions** — Facilitez le partage, l'invitation, l'exposition\n3. **Mesurez le K-factor** — Invitations envoyees x taux d'acceptation x taux d'activation\n4. **Optimisez chaque etape** — Plus d'invitations, meilleur taux d'acceptation, meilleure activation\n5. **Testez differents mecanismes** — Invitation par email, partage de lien, collaboration",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Referral Network** track le K-factor de votre programme et identifie les ambassadeurs les plus performants. Analysez quels mecanismes de partage generent le plus de conversions pour optimiser votre boucle virale.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
  {
    slug: 'anti-fraud',
    title: 'Prevenir la fraude au parrainage',
    duration: '3 min',
    glossaryTerms: ['referral-network'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La fraude au parrainage survient quand des utilisateurs exploitent votre programme pour obtenir des recompenses de maniere illegitime. Types de fraude courants :\n\n- **Auto-parrainage** — Creer de faux comptes pour se parrainer soi-meme\n- **Parrainage entre complices** — Deux personnes se parrainent mutuellement sans intention d'utiliser le produit\n- **Fermes a comptes** — Creation massive de comptes pour accumuler des recompenses\n- **Abus de code promo** — Partage public de codes censés etre prives",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "La fraude peut representer 15 a 30% des conversions d'un programme de parrainage non protege. Cela fausse vos metriques, gonfle artificiellement vos couts et degrade la confiance dans le programme.\n\nUn programme de parrainage qui recompense de faux utilisateurs est un gouffre financier. La detection precoce de la fraude est essentielle pour la viabilite du programme.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Verifiez les emails** — Exigez une verification d'email pour chaque compte filleul\n2. **Detectez les patterns** — Meme IP, meme appareil, meme adresse pour plusieurs comptes\n3. **Retardez les recompenses** — Ne distribuez qu'apres 7-30 jours d'utilisation active\n4. **Limitez les parrainages** — Maximum 10-20 parrainages par ambassadeur par mois\n5. **Auditez regulierement** — Passez en revue les top parrains et verifiez la qualite des filleuls",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Referral Network** integre un systeme de detection anti-fraude automatique. Les comptes suspects sont signales et les recompenses sont mises en attente de validation. Vous pouvez configurer les regles de detection selon votre programme.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
  {
    slug: 'your-referral',
    title: 'Referral Network dans FO',
    duration: '3 min',
    glossaryTerms: ['referral-network', 'kpi', 'marketing-dashboard'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le Referral Network de Feedback Orchestrator est votre plateforme de parrainage complete. Creez un programme, recrutez des ambassadeurs, suivez les conversions et detectez la fraude — le tout depuis une seule interface.",
      },
      {
        type: 'methodology',
        title: "Comment l'utiliser ?",
        content:
          "1. **Configurez votre programme** — Type de recompense, conditions de validation, limites anti-fraude\n2. **Invitez vos premiers ambassadeurs** — Vos clients les plus satisfaits sont les meilleurs candidats\n3. **Partagez les liens** — Chaque ambassadeur recoit un lien unique et personnalise\n4. **Suivez les performances** — Tableau de bord avec conversions, K-factor, top ambassadeurs\n5. **Detectez la fraude** — Le systeme anti-fraude signale les activites suspectes\n6. **Optimisez** — Ajustez les recompenses et les mecanismes selon les resultats",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur **Referral Network** pour configurer votre programme de parrainage. Le tableau de bord affiche vos metriques cles : nombre d'ambassadeurs actifs, conversions, K-factor et revenus generes par le parrainage. Les donnees sont aussi visibles dans le **Marketing Dashboard**.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
];
